import React, { useState, useCallback, useEffect } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import styled from 'styled-components'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import { RiZoomOutFill, RiZoomInFill } from 'react-icons/ri'
import { ScaleIcon } from '../Icons/ScaleIcon'
import { useAppointmentsAdministrationState } from '../../stores/Administration/AppointmentsAdministrationProvider'
import i18n from '../../translation/i18n'
import ScrollWrapper from '../Page/logix2020/ScrollWrapper'
import { MdOutlineBlock } from 'react-icons/md'
import { getIsoDate } from '../../utils/getIsoDate'

const StyledCalendarHeader = styled.div`
  height: 3rem;
  background-color: #518600;
  color: white;
  font-size: 1.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.3rem 0.5rem;
  z-index: 6;
  position: sticky;
  top: 0;

  .arrowIcon {
    color: white;
    font-size: 1.4rem;
    display: flex;
    align-self: center;

    &:hover {
      color: #a6e144;
      cursor: pointer;
    }
  }

  .zoomIcon {
    font-size: 1.2rem;
    margin: 0 5px;

    &:hover {
      color: #a6e144;
      cursor: pointer;
    }
  }
  @media (max-width: 800px) {
    font-size: 1rem !important;

    div {
      font-size: 1rem !important;
    }

    span {
      font-size: 1rem !important;
    }
  }
  @media (min-width: 1184px) and (max-width: 1280px) {
    font-size: 1rem !important;

    div,
    span {
      font-size: 1rem !important;
    }
  }
  @media (min-width: 701px) and (max-width: 674px) {
    font-size: 0.8rem !important;

    div,
    span {
      font-size: 0.8rem !important;
    }
  }
`

const StyledCalendarBody = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledCalendarRow = styled.div`
  display: flex;
  border-top: 1px dotted #c6c6c6;
  height: ${(props) => (props.isZoomedIn ? '180px' : '60px')};
`

const StyledHourBlock = styled.div`
  width: 3.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
`

const StyledEventsBlock = styled.div`
  flex-grow: 1;
  position: relative;
  border-left: 1px dotted #c6c6c6;
  /* Dynamic styles for locked blocks */
  background-color: ${(props) => (props.isLocked ? '#ffb3b3' : 'transparent')};
  cursor: ${(props) => (props.isLocked ? 'not-allowed' : 'pointer')};

  display: flex;
  align-items: center;
  justify-content: center;

  .block-icon {
    color: red;
    font-size: 24px;
    position: absolute;
  }
`

const StyledEvent = styled.div`
  position: absolute;
  height: ${(props) => props.eventDuration};
  width: 100%;
  top: ${(props) => props.eventStart};
  z-index: 5;
  background-color: ${(props) => (props.isLocked ? '#ffb3b3' : '#c5c3c3')};
  color: black;
  border-top: 1px solid white;
  border-radius: 5px;
  padding: 0 5px;
  font-size: ${(props) => props.fontSize};
  text-align: start;

  &:hover {
    cursor: ${(props) => (props.isLocked ? 'not-allowed' : 'pointer')};
    filter: brightness(0.8);
  }
`

const StyledScaleButton = styled.div`
  cursor: pointer;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  width: 40px;
  border: none;
  background-color: transparent;
  path {
    fill: white;
  }
  path:hover {
    fill: #a6e144;
  }

  .bubble {
    background: #a6e144;
    font-size: 8px;
    border-radius: 50px;
    height: 13px;
    width: 13px;
    text-align: center;
    line-height: 1.2;
    color: white;
    position: absolute;
    display: flex;
    bottom: 9px;
    right: 1px;
    align-items: center;
    justify-content: center;
  }
`

const DailyAppointmentsOverview = (props) => {
  const { date, selectedDateHoursArray, showSimpleHeader, setSelectedAction } =
    props

  const {
    selectedDays,
    setAppointmentDetails,
    setSelectedDate,
    setShowDailyAppointmentsCompareModal,
    setShowOverviewOverlay,
    selectedDate,
  } = useAppointmentsAdministrationState()

  const [isZoomedIn, setIsZoomed] = useState(false)

  useEffect(() => {
    return () => {
      selectedDateHoursArray.forEach((hour) => {
        hour.length = 0
      })
    }
  }, [date, selectedDateHoursArray])

  const formatSelectedDate = useCallback((dateString) => {
    if (!dateString) {
      return null
    }

    try {
      const dateToFormat =
        typeof dateString === 'string'
          ? dateString
          : getIsoDate(dateString).split('T')[0]

      const date = new Date(dateToFormat)

      if (isNaN(date.getTime())) {
        return null
      }
      return (
        <div>
          <span style={{ fontWeight: 'bold' }}>
            {date.toLocaleString(i18n.language, {
              month: 'long',
              day: '2-digit',
              year: 'numeric',
            })}
          </span>
        </div>
      )
    } catch (error) {
      console.error('Error formatting date:', error)
      return null
    }
  }, [])

  const createEventBubble = (
    event,
    startTime,
    endTime,
    indexRow,
    indexEvent
  ) => {
    let startHour = parseInt(startTime.substring(11, 13))
    let startMinute = parseInt(startTime.substring(14, 16))
    let endHour = parseInt(endTime.substring(11, 13))
    let endMinute = parseInt(endTime.substring(14, 16))

    let eventStart = isZoomedIn ? startMinute * 3 : startMinute
    let eventDuration = isZoomedIn
      ? (endHour * 60 + endMinute - (startHour * 60 + startMinute)) * 3
      : endHour * 60 + endMinute - (startHour * 60 + startMinute)
    let fontSize = eventDuration > 20 ? '12px' : '7px'

    // eventWidth and eventLeft can be used if we want to show the events as a scale ( staircase layout `-_ )
    // let eventWidth = 100 / selectedDateHoursArray[indexRow].length;
    // eventLeft = indexEvent * eventWidth;

    const popover = (
      <Popover id='popover-basic' style={{ pointerEvents: 'none' }}>
        <Popover.Header as='h3'>
          {formatSelectedDate(selectedDate)}
        </Popover.Header>
        <Popover.Body>
          <span>
            {startTime.substring(11, 16)} - {endTime.substring(11, 16)}
          </span>
        </Popover.Body>
      </Popover>
    )
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        key={indexEvent}
        placement='top'
        overlay={popover}
      >
        <StyledEvent
          key={indexEvent}
          fontSize={fontSize}
          eventStart={`${eventStart}px`}
          eventDuration={`${eventDuration}px`}
          onClick={() =>
            !showSimpleHeader && !event.isLocked && onEventClickHandler(event)
          }
          isLocked={event.isLocked}
        >
          {eventDuration >= 9 && (
            <span>
              {startTime.substring(11, 16)} - {endTime.substring(11, 16)}
              {event.isLocked && (
                <MdOutlineBlock style={{ color: 'red', fontSize: '16px' }} />
              )}
            </span>
          )}
        </StyledEvent>
      </OverlayTrigger>
    )
  }

  const onEventClickHandler = (event) => {
    setShowOverviewOverlay()
    setSelectedAction('')
    setAppointmentDetails(event)
  }

  const goToPreviousDay = () => {
    let currentDateIndex = selectedDays.findIndex((day) =>
      day[0].includes(date)
    )
    if (currentDateIndex > 0) {
      let newDate = selectedDays[currentDateIndex - 1][0].slice(0, 10)
      setSelectedDate(newDate)
    }
  }

  const goToNextDay = () => {
    let currentDateIndex = selectedDays.findIndex((day) =>
      day[0].includes(date)
    )
    if (currentDateIndex < selectedDays.length - 1) {
      let newDate = selectedDays[currentDateIndex + 1][0].slice(0, 10)
      setSelectedDate(newDate)
    }
  }
  return (
    <ScrollWrapper style={{ height: showSimpleHeader ? '75vh' : '410px' }}>
      <StyledCalendarHeader>
        <div>
          {isZoomedIn ? (
            <RiZoomOutFill
              className='zoomIcon'
              onClick={() => setIsZoomed((prev) => !prev)}
            />
          ) : (
            <RiZoomInFill
              className='zoomIcon'
              onClick={() => setIsZoomed((prev) => !prev)}
            />
          )}
        </div>
        {date && formatSelectedDate(date)}
        {!showSimpleHeader && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <StyledScaleButton
              onClick={() => setShowDailyAppointmentsCompareModal(true)}
            >
              <ScaleIcon />
              <span className='bubble'>{selectedDays?.length || 0}</span>
            </StyledScaleButton>
            <IoIosArrowBack
              className='arrowIcon mx-2'
              onClick={() => goToPreviousDay()}
            />
            <IoIosArrowForward
              className='arrowIcon'
              onClick={() => goToNextDay()}
            />
          </div>
        )}
      </StyledCalendarHeader>
      <StyledCalendarBody>
        {/* loop through hoursArray to get 24 hour rows */}
        {selectedDateHoursArray.map((hour, indexRow) => {
          const isLockedHour = hour.some((event) => event.isLocked)
          return (
            <StyledCalendarRow key={indexRow} isZoomedIn={isZoomedIn}>
              <StyledHourBlock>
                <span>{`${indexRow < 10 ? '0' : ''}${indexRow}:00`}</span>
              </StyledHourBlock>
              <StyledEventsBlock>
                {hour.map((event, indexEvent) =>
                  createEventBubble(
                    event,
                    event.startTime,
                    event.endTime,
                    indexRow,
                    indexEvent
                  )
                )}
                {isLockedHour && <MdOutlineBlock className='block-icon' />}
              </StyledEventsBlock>
            </StyledCalendarRow>
          )
        })}
      </StyledCalendarBody>
    </ScrollWrapper>
  )
}

export default DailyAppointmentsOverview
